if (!window.__PlanBuilderDeepLink) {
  window.__PlanBuilderDeepLink = true;

  // Attribute -> the values a deep link may set it to. The single source of truth:
  // validation, defaults, normalisation and canonical casing all read from here.
  //
  // Deliberately hardcoded rather than derived from the configurations_question
  // metaobjects: the quiz offers 2-5 meals/day, but a deep link must also be able to
  // express meals=1 (the Workday plan). The cost is that this map can drift from the
  // answers a merchant adds in the admin.
  const CONFIG_RULES = {
    days: { type: "enum", values: ["3", "5", "6"], required: true },
    meals: { type: "enum", values: ["1", "2", "3", "4", "5"], required: true },
    breakfast: { type: "bool", default: "0" },
    snacks: { type: "bool", default: "0" },
    allergens: { type: "bool", default: "0" },
    avoid: {
      type: "list",
      values: ["Celery", "Eggs", "Fish", "Gluten", "Milk", "Peanuts"],
      default: "",
    },
    preferences: {
      type: "list",
      values: ["Vegan", "Vegetarian", "Pescatarian", "Halal"],
      default: "",
    },
  };

  const KEYS = Object.keys(CONFIG_RULES);
  const TRUTHY = ["1", "true"];
  const FALSY = ["0", "false"];

  class InvalidLink extends Error {}

  const normalise = (raw, rule) => {
    const value = raw.trim();

    if (rule.type === "enum") {
      if (!rule.values.includes(value)) throw new InvalidLink();
      return value;
    }

    if (rule.type === "bool") {
      const lower = value.toLowerCase();
      if (TRUTHY.includes(lower)) return "1";
      if (FALSY.includes(lower)) return "0";
      throw new InvalidLink();
    }

    // list — every entry must be a known ingredient. An unknown one invalidates the
    // link rather than being dropped: silently discarding an allergen the link asked
    // for is the one failure this feature must never have.
    return value
      .split(",")
      .map((entry) => {
        const match = rule.values.find(
          (allowed) => allowed.toLowerCase() === entry.trim().toLowerCase()
        );
        if (!match) throw new InvalidLink();
        return match;
      })
      .join(",");
  };

  // Returns null when the URL is not a deep link, throws InvalidLink when it is a
  // broken one, otherwise returns the full attribute set.
  const parseParams = (search) => {
    const params = new URLSearchParams(search);
    if (!KEYS.some((key) => params.has(key))) return null;

    const attrs = {};
    for (const key of KEYS) {
      const rule = CONFIG_RULES[key];
      const raw = params.get(key);

      if (raw === null || raw.trim() === "") {
        if (rule.required) throw new InvalidLink();
        attrs[key] = rule.default;
        continue;
      }
      attrs[key] = normalise(raw, rule);
    }

    // An ingredient list means restrictions, whatever `allergens` claimed. The list is
    // the stronger and safer signal, so it wins rather than rejecting the link.
    if (attrs.avoid) attrs.allergens = "1";

    return attrs;
  };

  // Merchant-facing order metadata, not storefront copy — so not run through `t`.
  const labelsFor = (attrs) => ({
    days_label: `${attrs.days} Days`,
    meals_label: `${attrs.meals} Meal${attrs.meals === "1" ? "" : "s"}`,
    breakfast_label: attrs.breakfast === "1" ? "Yes" : "No",
    snacks_label: attrs.snacks === "1" ? "Yes" : "No",
    allergens_label: attrs.allergens === "1" ? "Yes" : "No",
    avoid_label: attrs.avoid,
    preferences_label: attrs.preferences,
  });

  class DeepLinkReconciler {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.backUrl = root.dataset.backUrl || "/";
      this.planHandle = root.dataset.planHandle || "";
    }

    cartAttributes() {
      const el = this.root.querySelector("[data-pb-cart-attributes]");
      if (!el) return {};
      try {
        return JSON.parse(el.textContent.trim()) || {};
      } catch (e) {
        console.error("[plan-builder] failed to parse cart attributes", e);
        return {};
      }
    }

    async run() {
      let attrs;
      try {
        attrs = parseParams(window.location.search);
      } catch (err) {
        if (err instanceof InvalidLink) {
          this.showLinkError();
          return;
        }
        throw err;
      }

      const current = this.cartAttributes();

      if (attrs === null) {
        // Not a deep link. Nothing to reconcile, but the plan this page renders is now
        // derived from the product, so keep cart.plan in step with it for the cart and
        // checkout downstream. Silent: no overlay, no re-render — Liquid already
        // rendered the right plan.
        this.lower();
        if (this.planHandle && current.plan !== this.planHandle) {
          // Arriving at plan B's page with no query params (nav link, collection, search,
          // back button) while the cart still holds plan A's journey. cart.attributes.plan
          // is the only pointer journey-resume.liquid has back to plan A — ask before this
          // write overwrites it, same as the deep-link `differs` branch below.
          //
          // current.plan !== this.planHandle already rules out this ever firing on the
          // customer's own resume destination, so there is no prompt loop risk.
          const sync = { plan: this.planHandle };
          if (window.JourneyResume?.hasJourney()) {
            await window.JourneyResume.ask();
            // Starting over drops the old plan's saved meals too, as in the deep-link
            // branch below.
            sync.selections = "";
          }
          this.updateCart(sync).catch((err) =>
            console.error("[plan-builder] failed to sync plan", err)
          );
        }
        return;
      }

      // The path decides the plan, not the query.
      attrs.plan = this.planHandle;
      const patch = { ...attrs, ...labelsFor(attrs) };

      const differs = Object.keys(patch).some(
        (key) => (current[key] || "") !== (patch[key] || "")
      );
      if (!differs) {
        this.stripParams();
        this.lower();
        return;
      }

      // A deep link opened straight from an email never passed a plan card, so the
      // buying journey's guard did not run. Ask here before the cart is patched.
      //
      // ask() resolves only if they choose to start over; choosing to resume navigates
      // away, so the patch below simply never happens.
      if (window.JourneyResume?.hasJourney()) {
        await window.JourneyResume.ask();
        // They chose to start over. The saved meals must go with the old config: on the
        // same plan the builder's plan guard lets them through, and restoreSelections()
        // re-keys them onto the new tier, so "Start a new plan" handed the half-built
        // plan straight back.
        patch.selections = "";
      }

      // Only say "restarting" if there was something to restart.
      const hadConfig = Boolean(current.plan || current.days || current.meals);
      if (hadConfig) this.showRestarting();

      let html;
      try {
        html = await this.patchAndRender(patch);
      } catch (err) {
        console.error("[plan-builder] deep link reconcile failed", err);
        window.location.reload();
        return;
      }

      // A section that fails to render comes back as null with an HTTP 200.
      if (!html) {
        window.location.reload();
        return;
      }

      this.swap(html);
      this.stripParams();
      this.lower();
    }

    // One request: the cart write and the re-render come back together.
    async patchAndRender(attributes) {
      const res = await fetch(`${window.routes.cart_update_url}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          attributes,
          sections: this.sectionId,
          // Set explicitly: the default infers context from the Referer header.
          sections_url: window.location.pathname,
        }),
      });
      if (!res.ok) throw new Error(`cart update failed: ${res.status}`);
      const data = await res.json();
      return data.sections?.[this.sectionId] || null;
    }

    async updateCart(attributes) {
      const res = await fetch(`${window.routes.cart_update_url}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ attributes }),
      });
      if (!res.ok) throw new Error(`cart update failed: ${res.status}`);
      return res.json();
    }

    swap(html) {
      const wrapper = document.getElementById(`shopify-section-${this.sectionId}`);
      const fresh = new DOMParser()
        .parseFromString(html, "text/html")
        .getElementById(`shopify-section-${this.sectionId}`);

      if (!wrapper || !fresh) {
        window.location.reload();
        return;
      }

      // The outgoing controller holds document/window listeners; orphaning it leaks
      // them and leaves a second keydown handler fighting the new one.
      const stale = wrapper.querySelector("[data-plan-builder]");
      if (stale?.__controller) {
        stale.__controller.destroy();
        stale.__controller = null;
      }

      wrapper.replaceWith(fresh);
      this.root = fresh.querySelector("[data-plan-builder]");

      // Scripts injected via DOMParser never execute, so the fresh section's pre-paint
      // script did not run. Hold the overlay up ourselves until we lower it.
      this.root?.classList.add("is-reconciling");
      window.__PlanBuilderInit?.(fresh);
      // The scroll-reveal observer (assets/component-reveal.js) only knows the nodes it
      // scanned at load. Without this the fresh [data-reveal] main stays at opacity 0
      // and the meal grid never appears.
      fresh.dispatchEvent(new CustomEvent("reveal:scan", { bubbles: true }));
    }

    stripParams() {
      const url = new URL(window.location.href);
      // Only ours — utm_* and friends stay on the URL for analytics.
      KEYS.forEach((key) => url.searchParams.delete(key));
      window.history.replaceState({}, "", url);
    }

    showRestarting() {
      const el = this.root.querySelector("[data-pb-reconcile-message]");
      if (el) el.textContent = el.dataset.restarting || "";
    }

    showLinkError() {
      this.lower();
      const modal = this.root.querySelector("[data-pb-link-error]");
      if (!modal) {
        window.location.href = this.backUrl;
        return;
      }
      modal.removeAttribute("hidden");
    }

    lower() {
      this.root?.classList.remove("is-reconciling");
    }
  }

  const start = () => {
    const root = document.querySelector("[data-plan-builder]");
    if (!root || root.__deepLink) return;
    root.__deepLink = true;

    new DeepLinkReconciler(root).run().catch((err) => {
      console.error("[plan-builder] deep link failed", err);
      root.classList.remove("is-reconciling");
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
}
